import { useState, useEffect } from 'react';
import { useApprovalStore } from '../../../store/approvalStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { CheckCircle, XCircle, Search, Clock, FileSignature, ShieldAlert, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';

const ENTITY_TYPES = ['budget', 'document', 'expense', 'pull-request', 'timesheet', 'other'];
const EMPTY_FORM = { title: '', description: '', entityType: 'budget' };

export function Approvals() {
    const { approvals, isLoading, fetchApprovals, createApproval, updateApproval, deleteApproval } = useApprovalStore();
    const [search, setSearch] = useState('');
    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchApprovals();
    }, []);

    const filteredApprovals = approvals.filter(a =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.entityType.toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => { setForm(EMPTY_FORM); setErrors({}); setFormOpen(true); };

    const handleSubmit = async () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        setErrors(next);
        if (Object.keys(next).length) return;
        setSaving(true);
        try {
            await createApproval({
                title: form.title.trim(),
                description: form.description.trim(),
                entityType: form.entityType,
                status: 'pending',
            });
            toast.success('Approval requested');
            setFormOpen(false);
        } catch (e) {
            toast.error('Failed to request approval');
        } finally {
            setSaving(false);
        }
    };

    const handleAction = async (id: string, action: 'approved' | 'rejected') => {
        try {
            await updateApproval(id, { status: action });
            toast.success(`Request ${action}`);
        } catch (e) {
            toast.error(`Failed to ${action} request`);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteApproval(deleteTarget.id);
            toast.success('Approval deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete approval');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Approvals</h1>
                    <p className="text-muted-foreground">Streamlined approval chains for pull requests, documents, and finances.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    Request Approval
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 min-h-0">
                <div className="md:col-span-1 space-y-4 overflow-y-auto">
                    <Card>
                        <CardHeader className="pb-3 border-b border-border">
                            <h3 className="text-foreground font-display text-card-title">My Queue</h3>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="flex flex-col">
                                <Button variant="ghost" className="justify-between rounded-none border-b border-border py-4 h-auto bg-primary/5">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-warning/20 text-warning flex items-center justify-center">
                                            <ShieldAlert className="w-4 h-4" />
                                        </div>
                                        <div className="text-left">
                                            <p className="font-medium text-sm text-foreground">Action Required</p>
                                            <p className="text-xs text-muted-foreground">Pending my approval</p>
                                        </div>
                                    </div>
                                    <Badge variant="warning">{approvals.filter(a => a.status === 'pending').length}</Badge>
                                </Button>
                                <Button variant="ghost" className="justify-between rounded-none border-b border-border py-4 h-auto">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-secondary text-foreground flex items-center justify-center">
                                            <Clock className="w-4 h-4" />
                                        </div>
                                        <div className="text-left">
                                            <p className="font-medium text-sm text-foreground">Requested by Me</p>
                                            <p className="text-xs text-muted-foreground">Waiting on others</p>
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium text-muted-foreground">0</span>
                                </Button>
                                <Button variant="ghost" className="justify-between rounded-none py-4 h-auto">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center">
                                            <CheckCircle className="w-4 h-4" />
                                        </div>
                                        <div className="text-left">
                                            <p className="font-medium text-sm text-foreground">Recently Completed</p>
                                            <p className="text-xs text-muted-foreground">Past 7 days</p>
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium text-muted-foreground">
                                        {approvals.filter(a => a.status === 'approved' || a.status === 'rejected').length}
                                    </span>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="md:col-span-2 flex flex-col min-h-0 bg-card border border-border rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-border flex justify-between items-center shrink-0">
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search requests..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm">Filter</Button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-4 space-y-4 bg-background/50">
                        {isLoading ? (
                            <div className="text-center text-muted-foreground py-8">Loading...</div>
                        ) : filteredApprovals.length === 0 ? (
                            <div className="text-center text-muted-foreground py-12 border-2 border-dashed border-border rounded-lg flex flex-col items-center">
                                <FileSignature className="w-12 h-12 mb-4 opacity-20" />
                                <p>No pending approvals found.</p>
                            </div>
                        ) : filteredApprovals.map(a => (
                            <Card key={a.id} className="hover:border-primary/50 transition-colors">
                                <CardContent className="p-4 flex flex-col sm:flex-row justify-between gap-4">
                                    <div className="space-y-2 flex-1">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary" className="capitalize">{a.entityType}</Badge>
                                            <span className="text-xs text-muted-foreground">
                                                Requested {new Date(a.createdAt).toLocaleDateString()}
                                            </span>
                                            {a.status !== 'pending' && (
                                                <Badge variant={a.status === 'approved' ? 'success' : 'destructive'} className="ml-auto capitalize">
                                                    {a.status}
                                                </Badge>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-foreground text-sm font-semibold">{a.title}</h4>
                                            <p className="text-sm text-muted-foreground mt-1">{a.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex sm:flex-col gap-2 shrink-0 items-center justify-center sm:border-l sm:border-border sm:pl-4">
                                        {a.status === 'pending' && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    className="text-success border-success/30 hover:bg-success/90 hover:text-success-foreground w-full sm:w-28"
                                                    onClick={() => handleAction(a.id, 'approved')}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="text-destructive border-destructive/30 hover:bg-destructive/90 hover:text-destructive-foreground w-full sm:w-28"
                                                    onClick={() => handleAction(a.id, 'rejected')}
                                                >
                                                    <XCircle className="w-4 h-4 mr-2" /> Reject
                                                </Button>
                                            </>
                                        )}
                                        <Button
                                            variant="ghost" size="sm"
                                            className="text-muted-foreground hover:text-destructive w-full sm:w-28"
                                            onClick={() => setDeleteTarget({ id: a.id, title: a.title })}
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>

            {/* Request approval modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Request Approval</DialogTitle>
                        <DialogDescription>Raise an approval request for review.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="e.g. Q3 budget revision" invalid={!!errors.title} />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Type</label>
                            <Select value={form.entityType} onValueChange={(v) => setForm(f => ({ ...f, entityType: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{ENTITY_TYPES.map(x => <SelectItem key={x} value={x} className="capitalize">{x.replace('-', ' ')}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Description</label>
                            <Textarea rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="What needs approval and why…" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>Submit Request</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete approval</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
